'use client';

import { useState, useTransition } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { FeatureSettingsSchema } from '@/schemas';
import { updateFeatureSettings } from '@/actions';
import { useTranslations } from '@/hooks/useTranslations';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ExtendedUser } from '@/types/next-auth';
import { userCan } from '@/lib/plans';
import { SettingsSectionWrapper } from './settings-section-wrapper';
import { FeatureToggleRow } from './_shared/feature-toggle-row';

interface FeatureSettingsFormProps {
  user: ExtendedUser;
}

export function FeatureSettingsForm({ user }: FeatureSettingsFormProps) {
  const t = useTranslations('UserSettings');
  const common = useTranslations('Common');

  const [error, setError] = useState<string | undefined>('');
  const [success, setSuccess] = useState<string | undefined>('');
  const [isPending, startTransition] = useTransition();

  const form = useForm<z.infer<typeof FeatureSettingsSchema>>({
    resolver: zodResolver(FeatureSettingsSchema),
    defaultValues: {
      enableExpenses: user.enableExpenses ?? false,
      enableProducts: user.enableProducts ?? true,
      overdueReminderEnabled: user.overdueReminderEnabled ?? false,
      overdueReminderDays: user.overdueReminderDays ?? 5,
    },
  });

  const reminderEnabled = form.watch('overdueReminderEnabled');
  const expensesLocked = !userCan(user, 'expenses');

  const onSubmit = (values: z.infer<typeof FeatureSettingsSchema>) => {
    setError('');
    setSuccess('');

    startTransition(() => {
      updateFeatureSettings(values).then((data) => {
        setError(data?.error);
        setSuccess(data?.success);
      });
    });
  };

  return (
    <SettingsSectionWrapper
      title={t('featuresTitle')}
      description={t('featuresDescription')}
      error={error}
      success={success}
      footer={
        <div className="flex gap-3">
          <Button
            type="submit"
            form="feature-settings-form"
            className="flex-1"
            disabled={isPending}
          >
            {isPending ? common('saving') || 'Saving...' : common('save')}
          </Button>
        </div>
      }
    >
      <form
        id="feature-settings-form"
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
      >
        <div className="space-y-4">
          <FeatureToggleRow
            id="enableExpenses"
            title={t('enableExpenses')}
            description={t('enableExpensesDescription')}
            checked={!!form.watch('enableExpenses')}
            onCheckedChange={(checked) =>
              form.setValue('enableExpenses', checked)
            }
            disabled={isPending}
            locked={expensesLocked}
            lockedBadge={t('proOnlyBadge')}
            lockedLinkLabel={t('upgradeToPro')}
          />

          <FeatureToggleRow
            id="enableProducts"
            title={t('enableProducts')}
            description={t('enableProductsDescription')}
            checked={!!form.watch('enableProducts')}
            onCheckedChange={(checked) =>
              form.setValue('enableProducts', checked)
            }
            disabled={isPending}
          />

          <FeatureToggleRow
            id="overdueReminderEnabled"
            title={t('overdueReminderEnabled')}
            description={t('overdueReminderDescription')}
            checked={reminderEnabled}
            onCheckedChange={(checked) =>
              form.setValue('overdueReminderEnabled', checked)
            }
            disabled={isPending}
          >
            {reminderEnabled && (
              <div className="mt-4 flex items-center gap-3 pt-4 border-t">
                <Label htmlFor="overdueReminderDays" className="text-sm">
                  {t('overdueReminderDaysLabel')}
                </Label>
                <Input
                  id="overdueReminderDays"
                  type="number"
                  min={1}
                  max={90}
                  className="w-24"
                  disabled={isPending}
                  {...form.register('overdueReminderDays', {
                    valueAsNumber: true,
                  })}
                />
                <span className="text-sm text-muted-foreground">
                  {t('days')}
                </span>
              </div>
            )}
          </FeatureToggleRow>
        </div>
      </form>
    </SettingsSectionWrapper>
  );
}
